// ---------------------------------------------------------------------------
// EA landing derivations — pure functions that turn the fan-out of
// per-principal API responses into the four cross-principal sections the EA
// landing leads on:
//
//   1. Plans awaiting approval (status = Proposed) + pending decisions
//   2. Follow-ups due today (type = FollowUp OR flag = FollowUp, not Done)
//   3. Focus-defence alerts (calendar conflicts on locked focus blocks)
//   4. Next meeting per principal
//
// Keeping these as plain functions (React-free) lets us unit-test them
// without mounting the page, same pattern as `utils/dashboard.ts` and
// `utils/intelligence.ts`.
// ---------------------------------------------------------------------------

import type {
  CalendarConflict,
  CalendarEvent,
  DailyPlan,
  Decision,
  WorkItem,
} from '../types/models';

export interface PrincipalRef {
  id: string;
  name: string;
}

// ---- 1. Awaiting approval -----------------------------------------------

export interface AwaitingApprovalRow {
  principal: PrincipalRef;
  kind: 'plan' | 'decision';
  /** Human-readable title: plan date, or decision title. */
  title: string;
  /** For decisions, keep the id so we can deep-link. */
  decisionId?: string;
}

export function deriveAwaitingApproval(
  inputs: Array<{
    principal: PrincipalRef;
    plan: DailyPlan | null;
    pendingDecisions: Decision[];
  }>,
): AwaitingApprovalRow[] {
  const rows: AwaitingApprovalRow[] = [];
  for (const { principal, plan, pendingDecisions } of inputs) {
    if (plan && plan.status === 'Proposed') {
      rows.push({
        principal,
        kind: 'plan',
        title: plan.dailyTheme?.trim() || `Plan for ${plan.date}`,
      });
    }
    for (const d of pendingDecisions) {
      rows.push({
        principal,
        kind: 'decision',
        title: d.title,
        decisionId: d.id,
      });
    }
  }
  return rows;
}

// ---- 2. Follow-ups due today --------------------------------------------

export interface FollowUpRow {
  principal: PrincipalRef;
  workItem: WorkItem;
  /** Who the follow-up is chasing, if captured on the item. */
  chasing?: string;
  /** ISO date the follow-up first appeared (original target date or creation). */
  since: string;
}

const TERMINAL_STATUSES = new Set(['Done', 'Cancelled']);

function isFollowUp(w: WorkItem): boolean {
  if (w.type === 'FollowUp') return true;
  if (Array.isArray(w.flags) && w.flags.includes('FollowUp')) return true;
  return false;
}

function followUpChasing(w: WorkItem): string | undefined {
  // Heuristic: pick the first word out of the notes or tags that looks like
  // a name. If the item has an `assignedToExecutiveId` we can't resolve the
  // display name here (the caller would know), so we surface nothing rather
  // than something wrong.
  const source = (w.notes ?? '').split('\n')[0]?.trim();
  if (source && source.length > 0 && source.length <= 80) return source;
  return undefined;
}

export function deriveFollowUpsDueToday(
  inputs: Array<{
    principal: PrincipalRef;
    workItems: WorkItem[];
  }>,
): FollowUpRow[] {
  const rows: FollowUpRow[] = [];
  for (const { principal, workItems } of inputs) {
    for (const w of workItems) {
      if (!isFollowUp(w)) continue;
      if (TERMINAL_STATUSES.has(w.status)) continue;
      rows.push({
        principal,
        workItem: w,
        chasing: followUpChasing(w),
        since: w.originalDate || w.createdAt || w.targetDate,
      });
    }
  }
  // Oldest first — the things that have been open longest are the ones
  // most likely to need an EA nudge.
  rows.sort((a, b) => a.since.localeCompare(b.since));
  return rows;
}

// ---- 3. Focus-defence alerts --------------------------------------------

export interface FocusDefenceRow {
  principal: PrincipalRef;
  conflict: CalendarConflict;
}

/**
 * A calendar conflict counts as a focus-defence alert when it touches a
 * labelled focus window. The backend attaches `focusWindowLabel` whenever a
 * conflict involves a focus block, so that's our canonical signal.
 */
function isFocusConflict(c: CalendarConflict): boolean {
  if (c.focusWindowLabel && c.focusWindowLabel.trim().length > 0) return true;
  // Fallback: descriptions of focus conflicts have "focus" in the text.
  return /focus/i.test(c.description ?? '');
}

export function deriveFocusDefenceAlerts(
  inputs: Array<{
    principal: PrincipalRef;
    conflicts: CalendarConflict[];
  }>,
): FocusDefenceRow[] {
  const rows: FocusDefenceRow[] = [];
  for (const { principal, conflicts } of inputs) {
    for (const conflict of conflicts) {
      if (!isFocusConflict(conflict)) continue;
      rows.push({ principal, conflict });
    }
  }
  // Hard conflicts first (harder to defend), then soft.
  rows.sort((a, b) => {
    if (a.conflict.severity === b.conflict.severity) return 0;
    return a.conflict.severity === 'Hard' ? -1 : 1;
  });
  return rows;
}

// ---- 4. Next meeting per principal --------------------------------------

export interface NextMeetingRow {
  principal: PrincipalRef;
  event: CalendarEvent | null;
}

export function deriveNextMeetings(
  inputs: Array<{
    principal: PrincipalRef;
    events: CalendarEvent[];
  }>,
  now: Date = new Date(),
): NextMeetingRow[] {
  const rows: NextMeetingRow[] = [];
  const nowMs = now.getTime();
  for (const { principal, events } of inputs) {
    const upcoming = events
      .filter((e) => !e.isAllDay)
      .filter((e) => new Date(e.endTime).getTime() >= nowMs)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    rows.push({ principal, event: upcoming[0] ?? null });
  }
  // Soonest meeting first; principals with no upcoming meeting fall to the end.
  rows.sort((a, b) => {
    const at = a.event ? new Date(a.event.startTime).getTime() : Infinity;
    const bt = b.event ? new Date(b.event.startTime).getTime() : Infinity;
    return at - bt;
  });
  return rows;
}
